import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { canonAPI } from '../lib/api';
import {
  BookOpen,
  Shield,
  Heart,
  Scale,
  Eye,
  RotateCcw,
  ChevronRight,
  FileText,
  Bird,
  GitBranch,
  Sun
} from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const principleIcons = {
  dignity: Shield,
  liberty: Bird,
  'non-harm': Heart,
  proportionality: Scale,
  accountability: Eye,
  reversibility: RotateCcw,
  separation: GitBranch,
  transparency: Sun,
};

export default function LibraryPage() {
  const [searchParams] = useSearchParams();
  const [parts, setParts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [principles, setPrinciples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('parts');
  const [selectedPrinciple, setSelectedPrinciple] = useState('dignity');
  const { t } = useTranslation();

  useEffect(() => {
    async function loadData() {
      try {
        const [partsRes, articlesRes, principlesRes] = await Promise.all([
          canonAPI.getParts(),
          canonAPI.getArticles(),
          canonAPI.getPrinciples(),
        ]);
        setParts(partsRes.data);
        setArticles(articlesRes.data);
        setPrinciples(principlesRes.data);

        const principleParam = searchParams.get('principle');
        if (principleParam) {
          setActiveTab('principles');
          setSelectedPrinciple(principleParam.toLowerCase());
        }
      } catch (error) {
        console.error('Failed to load library data:', error);
        console.error('Requested URL was:', error.config?.url);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [searchParams]);

  const getArticlesByPart = (partId) => {
    return articles.filter(a => a.part_id === partId);
  };

  const getArticlesByPrinciple = (principleName) => {
    const principle = principles.find(p => p.name.toLowerCase() === principleName.toLowerCase());
    if (!principle) return [];
    return articles.filter(a => principle.related_articles.includes(a.number));
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8" data-testid="library-loading">
        <div className="space-y-4">
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className="loading-pulse h-24 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="library-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
          <BookOpen className="h-4 w-4" />
          <span>{t('library.libraryMode')}</span>
        </div>
        <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          {t('library.title')}
        </h1>
        <p className="text-muted-foreground">
          {t('library.subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="parts" data-testid="tab-parts">
            <FileText className="h-4 w-4 mr-2" />
            {t('library.byPart')}
          </TabsTrigger>
          <TabsTrigger value="principles" data-testid="tab-principles">
            <Shield className="h-4 w-4 mr-2" />
            {t('library.byPrinciple')}
          </TabsTrigger>
        </TabsList>

        {/* By Part */}
        <TabsContent value="parts" data-testid="parts-content">
          <Accordion type="single" collapsible defaultValue="part-1" className="space-y-3">
            {parts.map((part, index) => (
              <AccordionItem
                key={part.id}
                value={part.id}
                className="border rounded-lg px-4"
                data-testid={`part-${part.id}`}
              >
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex flex-col items-start text-left">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">
                        {t('library.part')} {index + 1}
                      </span>
                      {part.amendment_level === 'extreme_supermajority' && (
                        <Badge variant="secondary" className="text-xs">{t('library.unamendable')}</Badge>
                      )}
                    </div>
                    <span className="font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
                      {part.name}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {part.articles}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <p className="text-sm text-muted-foreground mb-4">{part.description}</p>
                  <div className="space-y-2">
                    {getArticlesByPart(part.id).map((article) => (
                      <Link
                        key={article.number}
                        to={`/article/${article.number}`}
                        className="article-card flex items-center justify-between"
                        data-testid={`article-link-${article.number}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-xs text-muted-foreground">{t('library.article')} {article.number}</span>
                            {article.is_absolute && (
                              <Badge variant="outline" className="text-xs">{t('library.absolute')}</Badge>
                            )}
                          </div>
                          <h4 className="font-medium">{article.title}</h4>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </Link>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </TabsContent>

        {/* By Principle */}
        <TabsContent value="principles" data-testid="principles-content">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Principles List */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('library.principles')}</CardTitle>
                  <CardDescription>{t('library.principlesDesc')}</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    <div className="p-4 space-y-2">
                      {principles.map((principle) => {
                        const Icon = principleIcons[principle.name.toLowerCase()] || Shield;
                        const isSelected = selectedPrinciple === principle.name.toLowerCase();
                        return (
                          <button
                            key={principle.name}
                            onClick={() => setSelectedPrinciple(principle.name.toLowerCase())}
                            className={`w-full p-3 rounded-lg text-left transition-colors ${isSelected
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted'
                              }`}
                            data-testid={`principle-btn-${principle.name.toLowerCase()}`}
                          >
                            <div className="flex items-center space-x-3">
                              <Icon className="h-5 w-5" />
                              <div>
                                <div className="font-medium">{principle.name}</div>
                                <div className={`text-xs ${isSelected ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                  {principle.related_articles.length} articles
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>

            {/* Articles for Selected Principle */}
            <div className="lg:col-span-2">
              {selectedPrinciple ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center space-x-2">
                      {(() => {
                        const Icon = principleIcons[selectedPrinciple] || Shield;
                        return <Icon className="h-5 w-5 text-accent" />;
                      })()}
                      <CardTitle className="capitalize">{selectedPrinciple}</CardTitle>
                    </div>
                    <CardDescription>
                      {principles.find(p => p.name.toLowerCase() === selectedPrinciple)?.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-3 pr-4">
                        {getArticlesByPrinciple(selectedPrinciple).map((article) => (
                          <Link
                            key={article.number}
                            to={`/article/${article.number}`}
                            className="article-card block"
                            data-testid={`principle-article-${article.number}`}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center space-x-2 mb-1">
                                  <Badge variant="outline" className="text-xs">
                                    {t('library.article')} {article.number}
                                  </Badge>
                                  <Badge variant="secondary" className="text-xs">
                                    {article.part_name}
                                  </Badge>
                                  {article.is_absolute && (
                                    <Badge className="text-xs bg-amber-500/20 text-amber-700 dark:text-amber-400">
                                      {t('library.absolute')}
                                    </Badge>
                                  )}
                                </div>
                                <h4 className="font-medium mb-1">{article.title}</h4>
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {article.content}
                                </p>
                              </div>
                              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              ) : (
                <Card className="h-full flex items-center justify-center min-h-[300px]">
                  <CardContent className="text-center text-muted-foreground">
                    <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>{t('library.selectPrinciple')}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
